import type { FC } from 'react';
import { PrivacyStyles as styles } from '../css/styles';
import { Helmet } from 'react-helmet-async';

const UserAgreementEU: FC = () => {
    return (
        <>
            <Helmet>
                <title>Terms of Service — First Klass</title>
                <meta name="description" content="Terms of Service for First Klass platform (www.first-klass.ru) — European Union" />
            </Helmet>
            <div className={styles.privacyContainer}>
                <h1>Terms of Service</h1>
                <p>
                    These Terms of Service («Terms») govern your access to and use of the{' '}
                    <strong>First Klass</strong> platform, available at{' '}
                    <strong>www.first-klass.ru</strong> («Platform»), operated by the First Klass
                    administration («we», «us», or «the Operator»).
                </p>
                <p>
                    By registering an account or otherwise using the Platform, you confirm that
                    you have read, understood, and agree to be bound by these Terms. If you do not
                    agree, you must not use the Platform.
                </p>
                <p>
                    Where you are a consumer resident in the European Economic Area (EEA), these
                    Terms are provided in accordance with EU Directive 2011/83/EU on consumer rights
                    and other applicable EU legislation.
                </p>

                <h2>1. Definitions</h2>
                <p>
                    <strong>1.1. Platform</strong> — the First Klass web application and associated
                    services accessible at www.first-klass.ru, including any mobile applications.
                </p>
                <p>
                    <strong>1.2. User</strong> — any individual or legal entity that registers
                    for or uses the Platform.
                </p>
                <p>
                    <strong>1.3. Teacher</strong> — a User who creates and delivers educational
                    content or courses to Students through the Platform.
                </p>
                <p>
                    <strong>1.4. Student</strong> — a User who accesses educational content or
                    courses provided by a Teacher or Learning Organisation through the Platform.
                </p>
                <p>
                    <strong>1.5. Learning Organisation</strong> — a legal entity using the Platform
                    to deliver online education to Students.
                </p>
                <p>
                    <strong>1.6. Content</strong> — any material uploaded, created, or shared by
                    a User on the Platform, including text, images, audio, video, and course materials.
                </p>
                <p>
                    <strong>1.7. Subscription</strong> — a paid plan granting access to extended
                    Platform functionality for a defined period.
                </p>

                <h2>2. Account Registration</h2>
                <p>
                    2.1. You must be at least 16 years old to create an account. If you are under
                    18, you confirm that you have obtained parental or guardian consent.
                </p>
                <p>
                    2.2. You agree to provide accurate, current, and complete information during
                    registration and to keep it up to date. You are responsible for all activity
                    that occurs under your account.
                </p>
                <p>
                    2.3. You must keep your login credentials confidential and must not share
                    them with any third party. Notify us immediately at the contact address on
                    the Platform if you suspect unauthorised access to your account.
                </p>
                <p>
                    2.4. We reserve the right to suspend or terminate accounts that violate
                    these Terms, provide false information, or engage in harmful conduct.
                </p>

                <h2>3. Licence to Use the Platform</h2>
                <p>
                    3.1. Subject to these Terms, we grant you a personal, non-exclusive,
                    non-transferable, revocable licence to access and use the Platform for
                    your own educational or teaching purposes.
                </p>
                <p>
                    3.2. You may not sublicense, sell, resell, transfer, assign, or commercially
                    exploit any part of the Platform or your access to it.
                </p>
                <p>
                    3.3. All intellectual property rights in the Platform and its original content
                    remain with the Operator. Nothing in these Terms transfers any ownership
                    of intellectual property to you.
                </p>

                <h2>4. Acceptable Use</h2>
                <p>4.1. You agree not to use the Platform to:</p>
                <ul>
                    <li>upload or distribute unlawful, harmful, defamatory, or infringing Content;</li>
                    <li>impersonate another person or entity or misrepresent your affiliation;</li>
                    <li>transmit spam, unsolicited communications, or malicious software;</li>
                    <li>attempt to gain unauthorised access to any part of the Platform or its systems;</li>
                    <li>use automated tools to scrape, crawl, or extract data without our written consent;</li>
                    <li>engage in any activity that disrupts or impairs Platform performance.</li>
                </ul>
                <p>
                    4.2. We reserve the right to remove any Content and suspend access for Users
                    who breach these obligations, without prior notice where urgent action is required.
                </p>

                <h2>5. User Content</h2>
                <p>
                    5.1. You retain ownership of Content you upload to the Platform. By uploading
                    Content, you grant us a non-exclusive, worldwide, royalty-free licence to host,
                    store, display, and transmit that Content solely as necessary to provide the Platform services.
                </p>
                <p>
                    5.2. You warrant that your Content does not infringe any third-party intellectual
                    property rights, privacy rights, or applicable laws, and you indemnify us against
                    any claims arising from your Content.
                </p>

                <h2>6. Subscriptions and Payments</h2>
                <p>
                    6.1. Pricing, billing periods, and included features for each subscription plan
                    are detailed in the «Pricing» section of the Platform.
                </p>
                <p>
                    6.2. All prices are displayed inclusive of applicable VAT where required
                    by EU law. You are responsible for any additional taxes applicable in your
                    country of residence.
                </p>
                <p>
                    6.3. Subscriptions renew automatically at the end of each billing period unless
                    cancelled before the renewal date. You may cancel at any time through your
                    account settings.
                </p>
                <p>
                    6.4. <strong>Right of withdrawal (EU consumers):</strong> In accordance with
                    EU Directive 2011/83/EU, you have the right to withdraw from a subscription
                    contract within 14 days of purchase without giving a reason, provided you have
                    not yet commenced use of the paid service. If you have started using the service,
                    you acknowledge that your right of withdrawal is waived in respect of services
                    already performed.
                </p>
                <p>
                    6.5. Refund requests outside the withdrawal period are assessed on a case-by-case
                    basis. We do not issue refunds solely because a User has chosen not to use the
                    Platform during the subscription period.
                </p>

                <h2>7. Intellectual Property</h2>
                <p>
                    7.1. The Platform, including its design, software, trademarks, and original
                    content provided by the Operator, is protected by EU and international
                    intellectual property law. Unauthorised reproduction or distribution is prohibited.
                </p>
                <p>
                    7.2. You may not use our trade names, trademarks, logos, or domain names
                    without our prior written consent.
                </p>

                <h2>8. Limitation of Liability</h2>
                <p>
                    8.1. To the fullest extent permitted by applicable EU law, we exclude liability
                    for indirect, incidental, or consequential losses arising from your use of
                    the Platform.
                </p>
                <p>
                    8.2. Nothing in these Terms limits or excludes liability for death or personal
                    injury caused by our negligence, fraud or fraudulent misrepresentation, or any
                    other liability that cannot be excluded under applicable EU consumer law.
                </p>
                <p>
                    8.3. Our total aggregate liability for direct losses is limited to the amount
                    paid by you for the Subscription in the 12 months preceding the claim.
                </p>

                <h2>9. Force Majeure</h2>
                <p>
                    Neither party shall be liable for failure to perform obligations due to events
                    beyond their reasonable control, including natural disasters, war, pandemic,
                    governmental action, or widespread infrastructure failure. Obligations are
                    suspended for the duration of such events.
                </p>

                <h2>10. Governing Law and Dispute Resolution</h2>
                <p>
                    10.1. These Terms are governed by applicable law. As an EU consumer, you
                    benefit from any mandatory protections afforded by the law of your country
                    of habitual residence that cannot be derogated from by agreement.
                </p>
                <p>
                    10.2. In the event of a dispute, we encourage you to contact us first so
                    we can attempt to resolve the matter informally. EU consumers may also use
                    the European Commission's Online Dispute Resolution (ODR) platform at
                    ec.europa.eu/consumers/odr.
                </p>
                <p>
                    10.3. If informal resolution is not possible, disputes shall be submitted
                    to the courts of competent jurisdiction in accordance with applicable law.
                </p>

                <h2>11. Changes to These Terms</h2>
                <p>
                    We may update these Terms from time to time. We will give you reasonable
                    prior notice of material changes, where required by law. Continued use of
                    the Platform after the effective date of changes constitutes acceptance
                    of the updated Terms.
                </p>
                <p>
                    <strong>Version date: 14 April 2026</strong>
                </p>
            </div>
        </>
    );
};

export default UserAgreementEU;
